import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const MarketDataPanel = ({ symbol }) => {
  const [marketDepth, setMarketDepth] = useState({ bids: [], asks: [] });
  const [recentTrades, setRecentTrades] = useState([]);
  const [economicEvents, setEconomicEvents] = useState([]);
  const [activeTab, setActiveTab] = useState('depth');

  // Mock market depth data
  useEffect(() => {
    const generateMarketDepth = () => {
      const basePrice = 1.0850;
      const bids = [];
      const asks = [];

      for (let i = 0; i < 10; i++) {
        bids?.push({
          price: parseFloat((basePrice - (i + 1) * 0.0001)?.toFixed(5)),
          volume: Math.floor(Math.random() * 5000000) + 1000000,
          orders: Math.floor(Math.random() * 50) + 10
        });

        asks?.push({
          price: parseFloat((basePrice + (i + 1) * 0.0001)?.toFixed(5)),
          volume: Math.floor(Math.random() * 5000000) + 1000000,
          orders: Math.floor(Math.random() * 50) + 10
        });
      }

      setMarketDepth({ bids, asks });
    };

    generateMarketDepth();
    const interval = setInterval(generateMarketDepth, 2000);
    return () => clearInterval(interval);
  }, [symbol]);

  // Mock recent trades data
  useEffect(() => {
    const generateRecentTrades = () => {
      const trades = [];
      const basePrice = 1.0850;
      const now = new Date();

      for (let i = 0; i < 20; i++) {
        const price = basePrice + (Math.random() - 0.5) * 0.002;
        const volume = Math.floor(Math.random() * 1000000) + 100000;
        const side = Math.random() > 0.5 ? 'buy' : 'sell';
        const timestamp = new Date(now.getTime() - i * 30000);

        trades?.push({
          id: i,
          price: parseFloat(price?.toFixed(5)),
          volume,
          side,
          timestamp: timestamp?.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: false 
          })
        });
      }

      setRecentTrades(trades);
    };

    generateRecentTrades();
    const interval = setInterval(() => {
      setRecentTrades(prev => {
        const newTrade = {
          id: Date.now(),
          price: parseFloat((1.0850 + (Math.random() - 0.5) * 0.002)?.toFixed(5)),
          volume: Math.floor(Math.random() * 1000000) + 100000,
          side: Math.random() > 0.5 ? 'buy' : 'sell',
          timestamp: new Date()?.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            second: '2-digit',
            hour12: false 
          })
        };
        return [newTrade, ...prev?.slice(0, 19)];
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [symbol]);

  // Mock economic events
  useEffect(() => {
    const events = [
      {
        id: 1,
        time: "14:30",
        currency: "USD",
        event: "Non-Farm Payrolls",
        impact: "high",
        forecast: "185K",
        previous: "178K"
      },
      {
        id: 2,
        time: "15:00",
        currency: "EUR",
        event: "ECB Interest Rate Decision",
        impact: "high",
        forecast: "4.50%",
        previous: "4.50%"
      },
      {
        id: 3,
        time: "16:30",
        currency: "GBP",
        event: "GDP Growth Rate",
        impact: "medium",
        forecast: "0.2%",
        previous: "0.1%"
      },
      {
        id: 4,
        time: "18:00",
        currency: "JPY",
        event: "BoJ Policy Meeting",
        impact: "high",
        forecast: "-0.10%",
        previous: "-0.10%"
      }
    ];

    setEconomicEvents(events);
  }, []);

  const formatVolume = (volume) => {
    if (volume >= 1000000) {
      return `${(volume / 1000000)?.toFixed(1)}M`;
    }
    return `${(volume / 1000)?.toFixed(0)}K`;
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return 'text-red-600 bg-red-100 dark:bg-red-900/20';
      case 'medium': return 'text-amber-600 bg-amber-100 dark:bg-amber-900/20';
      case 'low': return 'text-green-600 bg-green-100 dark:bg-green-900/20';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const tabs = [
    { id: 'depth', label: 'Market Depth', icon: 'BarChart3' },
    { id: 'trades', label: 'Recent Trades', icon: 'Activity' },
    { id: 'events', label: 'Economic Calendar', icon: 'Calendar' }
  ];

  return (
    <div className="w-full h-full bg-card border-l border-border flex flex-col">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex-1 flex items-center justify-center p-3 text-sm font-medium transition-colors ${
                activeTab === tab?.id
                  ? 'text-primary border-b-2 border-primary bg-muted/50' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} className="mr-2" />
              <span className="hidden sm:inline">{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'depth' && (
          <div className="h-full flex flex-col">
            {/* Market Depth Header */}
            <div className="p-3 border-b border-border">
              <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                <span>Price</span>
                <span>Volume</span>
                <span>Orders</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {/* Asks (Sell Orders) */}
              <div className="space-y-1 p-2">
                {marketDepth?.asks?.slice()?.reverse()?.map((ask, index) => (
                  <div key={`ask-${index}`} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-red-50 dark:hover:bg-red-900/10">
                    <span className="font-mono text-red-600">{ask?.price}</span>
                    <span className="font-mono text-muted-foreground">{formatVolume(ask?.volume)}</span>
                    <span className="text-muted-foreground">{ask?.orders}</span>
                  </div>
                ))}
              </div>

              {/* Spread */}
              <div className="py-2 px-4 bg-muted/50 border-y border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Spread:</span>
                  <span className="font-mono font-medium">
                    {marketDepth?.asks?.length > 0 && marketDepth?.bids?.length > 0
                      ? (marketDepth?.asks?.[0]?.price - marketDepth?.bids?.[0]?.price)?.toFixed(5)
                      : '0.00000'
                    }
                  </span>
                </div>
              </div>

              {/* Bids (Buy Orders) */}
              <div className="space-y-1 p-2">
                {marketDepth?.bids?.map((bid, index) => (
                  <div key={`bid-${index}`} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-green-50 dark:hover:bg-green-900/10">
                    <span className="font-mono text-green-600">{bid?.price}</span>
                    <span className="font-mono text-muted-foreground">{formatVolume(bid?.volume)}</span>
                    <span className="text-muted-foreground">{bid?.orders}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'trades' && (
          <div className="h-full flex flex-col">
            {/* Recent Trades Header */}
            <div className="p-3 border-b border-border">
              <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
                <span>Time</span>
                <span>Price</span>
                <span>Volume</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="space-y-1 p-2">
                {recentTrades?.map((trade) => (
                  <div key={trade?.id} className="flex items-center justify-between text-xs py-1 px-2 rounded hover:bg-muted">
                    <span className="text-muted-foreground font-mono">{trade?.timestamp}</span>
                    <span className={`font-mono font-medium ${
                      trade?.side === 'buy' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {trade?.price}
                    </span>
                    <span className="text-muted-foreground font-mono">{formatVolume(trade?.volume)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'events' && (
          <div className="h-full overflow-y-auto">
            <div className="p-3">
              <div className="space-y-3">
                {economicEvents?.map((event) => (
                  <div key={event?.id} className="p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-foreground">{event?.time}</span>
                        <span className="px-2 py-1 text-xs font-medium bg-muted text-muted-foreground rounded">
                          {event?.currency}
                        </span>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getImpactColor(event?.impact)}`}>
                        {event?.impact?.toUpperCase()}
                      </span>
                    </div>
                    
                    <h4 className="text-sm font-medium text-foreground mb-2">{event?.event}</h4>
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex space-x-4">
                        <span>Forecast: <span className="font-mono">{event?.forecast}</span></span>
                        <span>Previous: <span className="font-mono">{event?.previous}</span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketDataPanel;